import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Copy, Settings2, PlusCircle, Plus, Trash2, RotateCcw, Truck, X, Save, TrendingUp, Boxes, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PriceListsManager from "@/components/PriceListsManager";
import ShippingPickerModal from "@/components/ShippingPickerModal";
import StockPickerModal from "@/components/StockPickerModal";

const BOX_TYPES = [
  { key: "abox", label: "A Box (Standart Amerikan)", enabled: true },
  { key: "plaka", label: "Düz Plaka", enabled: true },
  { key: "half_lid", label: "Yarım Kapak", enabled: true },
  { key: "full_lid", label: "Tam Kapak", enabled: true },
];

// Kâr Marjı matrisi: %15'ten başlayıp 2.5'lik artışlarla %100'e kadar
const MARGIN_STEPS = Array.from({ length: 35 }, (_, i) => 15 + i * 2.5);

// A Box formülü:
//   Levha Eni  = W + H + 2  (E/B/C/EB dalga) veya  W + H + 4  (BC dalga)
//   Levha Boyu = ((L + W) × 2) + 30
//   Levha m²   = Eni × Boyu / 1.000.000
function calcABox(L, W, H, flute, doublePiece = false) {
  const l = Number(L) || 0;
  const w = Number(W) || 0;
  const h = Number(H) || 0;
  const f = String(flute || "").trim().toUpperCase();
  const eniPad = f === "BC" ? 4 : 2;
  const sheetWidth = w + h + eniPad;
  const sheetLength = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  const sheetM2 = (sheetWidth * sheetLength) / 1_000_000;
  return {
    sheetWidth: Math.round(sheetWidth * 100) / 100,
    sheetLength: Math.round(sheetLength * 100) / 100,
    sheetM2: Math.round(sheetM2 * 1_000_000) / 1_000_000,
    eniPad,
    flute: f,
    doublePiece,
  };
}

// Yarım Kapak formülü (A Box ile aynı Boyu mantığı):
//   Levha Eni  = (W / 2) + H + 3
//   Levha Boyu = ((L + W) × 2) + 30   (çift parçaysa +30 → toplam +60)
//   Levha m²   = Eni × Boyu / 1.000.000
function calcHalfLid(L, W, H, doublePiece = false) {
  const l = Number(L) || 0;
  const w = Number(W) || 0;
  const h = Number(H) || 0;
  const sheetWidth = w / 2 + h + 3;
  const sheetLength = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  const sheetM2 = (sheetWidth * sheetLength) / 1_000_000;
  return {
    sheetWidth: Math.round(sheetWidth * 100) / 100,
    sheetLength: Math.round(sheetLength * 100) / 100,
    sheetM2: Math.round(sheetM2 * 1_000_000) / 1_000_000,
    eniPad: 3,
    flute: "",
    halfLid: true,
    doublePiece,
  };
}

// Tam Kapak formülü:
//   Levha Eni  = W + H
//   Levha Boyu = ((L + W) × 2) + 30   (çift parçaysa +30 → toplam +60)
//   Levha m²   = Eni × Boyu / 1.000.000
function calcFullLid(L, W, H, doublePiece = false) {
  const l = Number(L) || 0;
  const w = Number(W) || 0;
  const h = Number(H) || 0;
  const sheetWidth = w + h;
  const sheetLength = (l + w) * 2 + 30 + (doublePiece ? 30 : 0);
  const sheetM2 = (sheetWidth * sheetLength) / 1_000_000;
  return {
    sheetWidth: Math.round(sheetWidth * 100) / 100,
    sheetLength: Math.round(sheetLength * 100) / 100,
    sheetM2: Math.round(sheetM2 * 1_000_000) / 1_000_000,
    eniPad: 0,
    flute: "",
    fullLid: true,
    doublePiece,
  };
}

const fmtNum = (n, digits = 2) =>
  Number(n || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export default function Pricing() {
  const { user } = useAuth();
  const isPricingOnly = user?.role === "pricing_only";
  const [boxType, setBoxType] = useState("abox");
  const [L, setL] = useState("");
  const [W, setW] = useState("");
  const [H, setH] = useState("");
  // Düz Plaka için doğrudan levha ölçüsü
  const [plakaW, setPlakaW] = useState("");
  const [plakaL, setPlakaL] = useState("");
  // Koli çift parça mı? — A Box & Yarım Kapak için Levha Boyu'na +30 mm ekler.
  const [doublePiece, setDoublePiece] = useState(false);
  const [qty, setQty] = useState("1");

  // Fiyat listesi entegrasyonu
  const [priceLists, setPriceLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [activeListItems, setActiveListItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [managerOpen, setManagerOpen] = useState(false);
  // Karşılaştırma: sabitlenmiş hesaplar
  const [comparisons, setComparisons] = useState([]);
  // TEKLİF SEPETİ — çok kalemli teklif için hesaplanan ürünler
  const [cart, setCart] = useState([]);
  // Nakliye satırları (Fiyat Hesapla ekranındaki bu hesabın nakliye ekleri)
  const [shippingLines, setShippingLines] = useState([]);
  const [shipPickerOpen, setShipPickerOpen] = useState(false);
  // Cari eşleştirmesi (opsiyonel) — teklife kaydetmek için
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [savingQuote, setSavingQuote] = useState(false);
  // Kâr marjı (%) — yalnız kartona uygulanır; nakliye maliyeti sabit kalır
  const [marginPct, setMarginPct] = useState(15);
  // ADIM 3 — Stok bağı (opsiyonel)
  const [stocks, setStocks] = useState([]);
  const [selectedStockId, setSelectedStockId] = useState("");
  const [stockPickerOpen, setStockPickerOpen] = useState(false);
  // Ekstra bedeller (KALIP, KLİŞE, NAKLİYE, PALET) — teklife eklenir + PDF'te ayrı tablo
  const [extras, setExtras] = useState({
    mold_cost: "",
    cliche_cost: "",
    extra_shipping_cost: "",
    pallet_cost_per_unit: "",
    pallet_deposit: true,
  });
  // KDV & Ödeme Koşulları — teklife eklenir + PDF'te alt panelde yer alır
  const [terms, setTerms] = useState({
    vat_rate: 20,               // Türkiye varsayılan %20
    payment_term_days: 0,       // Vade (gün); 0 = peşin
    down_payment_pct: 0,        // Peşinat (%)
    advance_payment_pct: 0,     // Avans (%)
  });

  const loadPriceData = async () => {
    try {
      const [lists, prices] = await Promise.all([
        api.get("/pricing/lists"),
        api.get(activeListId ? `/pricing/prices?list_id=${activeListId}` : "/pricing/prices"),
      ]);
      setPriceLists(lists.data || []);
      const items = (prices.data && prices.data.items) || [];
      setActiveListItems(items);
      if (prices.data?.list?.id && !activeListId) setActiveListId(prices.data.list.id);
    } catch (e) { /* sessiz */ }
  };
  useEffect(() => { loadPriceData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (isPricingOnly) return;                 // pricing_only kullanıcı /contacts ve /stocks çekmez
    api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
    api.get("/stocks").then((r) => setStocks(r.data || [])).catch(() => {});
  }, [isPricingOnly]);
  useEffect(() => {
    if (activeListId) {
      api.get(`/pricing/prices?list_id=${activeListId}`).then((r) => {
        setActiveListItems((r.data && r.data.items) || []);
        setSelectedItemId("");
      });
    }
  }, [activeListId]);

  const selectedItem = useMemo(
    () => activeListItems.find((x) => x.id === selectedItemId) || null,
    [activeListItems, selectedItemId]
  );
  const calc = useMemo(() => {
    if (boxType === "abox") return calcABox(L, W, H, selectedItem?.flute, doublePiece);
    if (boxType === "half_lid") return calcHalfLid(L, W, H, doublePiece);
    if (boxType === "full_lid") return calcFullLid(L, W, H, doublePiece);
    if (boxType === "plaka") {
      const sw = Number(plakaW) || 0;
      const sl = Number(plakaL) || 0;
      if (!sw || !sl) return null;
      return {
        sheetWidth: Math.round(sw * 100) / 100,
        sheetLength: Math.round(sl * 100) / 100,
        sheetM2: Math.round((sw * sl) / 1_000_000 * 1_000_000) / 1_000_000,
        eniPad: 0,
        flute: "",
        plaka: true,
      };
    }
    return null;
  }, [boxType, L, W, H, selectedItem, plakaW, plakaL, doublePiece]);

  const quantity = Math.max(1, Number(qty) || 1);
  const totalM2 = calc ? calc.sheetM2 * quantity : 0;
  const activeList = useMemo(
    () => priceLists.find((x) => x.id === activeListId) || null,
    [priceLists, activeListId]
  );
  const discountPct = Number(activeList?.discount_percent) || 0;
  const listPrice = selectedItem ? Number(selectedItem.price) : 0;
  const unitCost = selectedItem ? listPrice * (1 - discountPct / 100) : 0;
  const totalCost = selectedItem && calc ? totalM2 * unitCost : 0;
  const discountSavings = selectedItem && calc && discountPct > 0 ? totalM2 * (listPrice - unitCost) : 0;

  // Nakliye toplamları
  const shippingTotal = shippingLines.reduce((s, x) => s + Number(x.total_cost || 0), 0);
  const shippingPerUnit = quantity > 0 ? shippingTotal / quantity : 0;
  const grandTotal = totalCost + shippingTotal; // Maliyet Toplam (Karton Maliyeti + Nakliye)
  // Kâr marjı hesabı — sadece karton üzerine uygulanır
  const marginPctNum = Number(marginPct) || 0;
  const kartonSalePrice = totalCost * (1 + marginPctNum / 100);
  const profitTL = kartonSalePrice - totalCost;
  const saleGrandTotal = kartonSalePrice + shippingTotal; // Satış Toplam (Karton Satış + Nakliye)
  const salePerUnit = quantity > 0 ? saleGrandTotal / quantity : 0;
  const removeShippingLine = (idx) =>
    setShippingLines((prev) => prev.filter((_, i) => i !== idx));
  const addShippingLine = (line) =>
    setShippingLines((prev) => [...prev, { ...line, _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);

  const pinComparison = () => {
    if (!calc || !selectedItem || !activeList) { toast.error("Önce ölçü + karton türü seçin"); return; }
    const dims = boxType === "plaka" ? `Plaka ${plakaW}×${plakaL}` : `${L}×${W}×${H}`;
    // Aynı tedarikçi + karton + ölçü + adet zaten sabitlendiyse tekrar ekleme
    const key = `${activeList.id}|${selectedItem.id}|${dims}|${quantity}|${shippingTotal.toFixed(2)}`;
    if (comparisons.some((c) => c.key === key)) { toast.info("Bu kombinasyon zaten karşılaştırmada"); return; }
    setComparisons((prev) => [
      ...prev,
      {
        key,
        pinned_at: Date.now(),
        supplier: activeList.supplier_name,
        supplier_valid_from: activeList.valid_from,
        dims, quantity,
        flute: selectedItem.flute,
        quality: selectedItem.quality,
        unit: selectedItem.unit,
        list_price: listPrice,
        discount_percent: discountPct,
        unit_cost: unitCost,
        sheet_m2: calc.sheetM2,
        total_m2: totalM2,
        total_cost: totalCost,
        savings: discountSavings,
        shipping_total: shippingTotal,
        shipping_per_unit: shippingPerUnit,
        shipping_lines_count: shippingLines.length,
        grand_total: grandTotal,
      },
    ]);
    toast.success("Karşılaştırmaya eklendi");
  };
  const removePin = (key) => setComparisons((prev) => prev.filter((c) => c.key !== key));
  const clearAllPins = () => {
    if (!window.confirm("Tüm karşılaştırmalar temizlensin mi?")) return;
    setComparisons([]);
  };

  // -------- Sepet (çok kalemli teklif) yardımcıları
  const addCurrentToCart = (customTitle) => {
    if (!calc || !selectedItem || !activeList) {
      toast.error("Önce ölçü + karton türü seçin");
      return;
    }
    const item = {
      _uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title: (customTitle || "").trim() || null,
      box_type: boxType,
      double_piece: (boxType === "abox" || boxType === "half_lid" || boxType === "full_lid") ? !!doublePiece : false,
      length_mm: Number(L) || null,
      width_mm: Number(W) || null,
      height_mm: Number(H) || null,
      sheet_width_mm: calc.sheetWidth,
      sheet_length_mm: calc.sheetLength,
      sheet_m2: calc.sheetM2,
      quantity,
      total_m2: totalM2,
      flute: selectedItem.flute,
      quality: selectedItem.quality,
      unit: selectedItem.unit,
      supplier_name: activeList?.supplier_name || null,
      supplier_valid_from: activeList?.valid_from || null,
      // Satış rakamları (marj sonrası) — teklife bu şekilde gider
      unit_cost: totalM2 > 0 ? (totalCost * (1 + marginPctNum / 100)) / totalM2 : 0,
      karton_total: totalCost * (1 + marginPctNum / 100),
      // Maliyet snapshot (arşiv)
      cost_unit: unitCost,
      cost_total: totalCost,
      // Stok bağı
      stock_id: selectedStock ? selectedStock.id : null,
      stock_sku: selectedStock ? selectedStock.sku : null,
      stock_name: selectedStock ? selectedStock.name : null,
    };
    setCart((prev) => [...prev, item]);
    toast.success("Sepete eklendi");
  };
  const removeFromCart = (uid) => setCart((prev) => prev.filter((x) => x._uid !== uid));
  const updateCartItem = (uid, patch) =>
    setCart((prev) => prev.map((x) => (x._uid === uid ? { ...x, ...patch } : x)));
  const editCartItem = (item) => {
    // Sepetteki bir kalemi hesaplayıcıya geri yükle ve sepetten çıkar.
    setBoxType(item.box_type || "abox");
    if (item.box_type === "plaka") {
      setPlakaW(item.sheet_width_mm != null ? String(item.sheet_width_mm) : "");
      setPlakaL(item.sheet_length_mm != null ? String(item.sheet_length_mm) : "");
      setL(""); setW(""); setH("");
    } else {
      setL(item.length_mm != null ? String(item.length_mm) : "");
      setW(item.width_mm != null ? String(item.width_mm) : "");
      setH(item.height_mm != null ? String(item.height_mm) : "");
      setPlakaW(""); setPlakaL("");
    }
    setQty(String(item.quantity || 1));
    // Karton eşleştir (flute+quality) — aktif liste değişmişse bulunamayabilir
    const normQ = (s) => String(s || "").toLocaleUpperCase("tr").replace(/[-_/·|]/g, " ").replace(/\s+/g, " ").trim();
    const normF = (s) => String(s || "").toLocaleUpperCase("tr").trim();
    const match = activeListItems.find(
      (x) => normF(x.flute) === normF(item.flute)
          && normQ(x.quality) === normQ(item.quality)
    );
    if (match) setSelectedItemId(match.id);
    if (item.stock_id) setSelectedStockId(item.stock_id);
    removeFromCart(item._uid);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Sepetten hesaba yüklendi — düzenleyip tekrar ekleyin");
  };
  const clearCart = () => setCart([]);
  const cartKartonTotal = useMemo(
    () => cart.reduce((s, i) => s + Number(i.karton_total || 0), 0),
    [cart]
  );

  const cheapestKey = useMemo(() => {
    if (comparisons.length < 2) return null;
    // Sıralama Satış Toplam bazında (aynı marj tüm satırlara uygulandığı için Maliyet ile aynı sıra)
    return comparisons.reduce((min, c) => {
      const sm = min.total_cost * (1 + marginPctNum / 100) + (min.shipping_total || 0);
      const sc = c.total_cost * (1 + marginPctNum / 100) + (c.shipping_total || 0);
      return sc < sm ? c : min;
    }, comparisons[0]).key;
  }, [comparisons, marginPctNum]);

  const reset = () => { setL(""); setW(""); setH(""); setPlakaW(""); setPlakaL(""); setQty("1"); setDoublePiece(false); };

  // ADIM 3 — Stoktan Seç: ölçü + kutu tipi + karton (flute+quality eşleşen aktif liste item'ı) otomatik dolar
  // Ana kalite eşleşmezse alternative_qualities içinde sırayla arama yapar (kullanıcı isteği)
  const applyStock = (stock) => {
    if (!stock) return;
    setBoxType(stock.box_type || "abox");
    if (stock.box_type === "plaka") {
      // Düz Plaka: L/W/H yok, doğrudan levha ölçüleri
      setL(""); setW(""); setH("");
      setPlakaW(stock.sheet_width_mm != null ? String(stock.sheet_width_mm) : "");
      setPlakaL(stock.sheet_length_mm != null ? String(stock.sheet_length_mm) : "");
    } else {
      // A Box / diğer: L/W/H
      setL(String(stock.length_mm ?? ""));
      setW(String(stock.width_mm ?? ""));
      setH(String(stock.height_mm ?? ""));
      setPlakaW(""); setPlakaL("");
    }
    setSelectedStockId(stock.id);
    if (stock.flute) {
      // Boşluk/tire/çoklu-space normalize: "IKL100 F80" ↔ "IKL100  F80" ↔ "IKL100-F80" eşleşsin
      const normQ = (s) => String(s || "")
        .toLocaleUpperCase("tr")
        .replace(/[-_/·|]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      const normF = (s) => String(s || "").toLocaleUpperCase("tr").trim();
      const flute = normF(stock.flute);
      const tryQualities = [stock.quality, ...(stock.alternative_qualities || [])]
        .filter(Boolean).map(normQ);
      let picked = null;
      let pickedQuality = null;
      for (const q of tryQualities) {
        const m = activeListItems.find(
          (it) => normF(it.flute) === flute && normQ(it.quality) === q
        );
        if (m) { picked = m; pickedQuality = q; break; }
      }
      if (picked) {
        setSelectedItemId(picked.id);
        const isMain = pickedQuality === normQ(stock.quality);
        if (isMain) {
          toast.success(`Stok yüklendi: ${stock.sku} · Karton eşleşti (${stock.flute}·${stock.quality})`);
        } else {
          toast.success(`Stok yüklendi: ${stock.sku} · Alternatif karton seçildi (${stock.flute}·${pickedQuality})`);
        }
      } else {
        setSelectedItemId("");
        const allQ = tryQualities.join(" / ");
        toast.warning(`Stok yüklendi ama aktif listede eşleşen karton yok (${flute} · ${allQ}). Manuel seçin.`);
      }
    } else {
      toast.success(`Stok yüklendi: ${stock.sku}`);
    }
  };

  const clearStock = () => setSelectedStockId("");
  const selectedStock = stocks.find((s) => s.id === selectedStockId);

  const saveQuote = async () => {
    if (!selectedContactId) { toast.error("Önce cari seçin"); return; }
    const useCart = cart.length > 0;
    if (!useCart && (!calc || !selectedItem || !activeList)) {
      toast.error("Ölçü ve karton türü seçili olmalı veya sepete ürün ekleyin");
      return;
    }
    setSavingQuote(true);
    try {
      // Payload — sepet varsa items[] gönder; sepet yoksa mevcut hesabı tek kalemli
      // items[] olarak sar. Böylece cost_unit / cost_total (tedarikçi maliyeti) her zaman
      // kayıt altında olur ve Tedarikçi Siparişi dialog'unda "Birim" doğru gelir.
      const singleItem = !useCart && calc && selectedItem ? [{
        title: null,
        box_type: boxType,
        length_mm: Number(L) || null,
        width_mm: Number(W) || null,
        height_mm: Number(H) || null,
        sheet_width_mm: calc.sheetWidth,
        sheet_length_mm: calc.sheetLength,
        sheet_m2: calc.sheetM2,
        quantity,
        total_m2: totalM2,
        flute: selectedItem.flute,
        quality: selectedItem.quality,
        unit: selectedItem.unit,
        supplier_name: activeList?.supplier_name || null,
        supplier_valid_from: activeList?.valid_from || null,
        // Satış tarafı (marj sonrası)
        unit_cost: totalM2 > 0 ? kartonSalePrice / totalM2 : 0,
        karton_total: kartonSalePrice,
        // Maliyet snapshot (tedarikçi, marj öncesi) — Sipariş dialog'u bunu okur
        cost_unit: unitCost,
        cost_total: totalCost,
        stock_id: selectedStock?.id || null,
        stock_sku: selectedStock?.sku || null,
        stock_name: selectedStock?.name || null,
      }] : [];
      const items = useCart ? cart.map(({ _uid, ...rest }) => rest) : singleItem;
      const kartonTotalToSave = useCart ? cartKartonTotal : kartonSalePrice;
      const grandTotalToSave = kartonTotalToSave + shippingTotal;
      // Tek kalemli akışta top-level alanlar (mevcut yapı). Sepet varsa referans olarak ilk ürünü ver.
      const primary = useCart ? cart[0] : null;
      await api.post("/price-quotes", {
        contact_id: selectedContactId,
        date: todayISO(),
        box_type: primary?.box_type || boxType,
        length_mm: primary?.length_mm ?? (Number(L) || 0),
        width_mm: primary?.width_mm ?? (Number(W) || 0),
        height_mm: primary?.height_mm ?? (Number(H) || 0),
        quantity: primary?.quantity ?? quantity,
        sheet_m2: primary?.sheet_m2 ?? (calc?.sheetM2 || 0),
        total_m2: primary?.total_m2 ?? totalM2,
        supplier_name: primary?.supplier_name || activeList?.supplier_name || null,
        supplier_valid_from: primary?.supplier_valid_from || activeList?.valid_from || null,
        flute: primary?.flute || selectedItem?.flute,
        quality: primary?.quality || selectedItem?.quality,
        unit: primary?.unit || selectedItem?.unit,
        stock_id: primary?.stock_id || (selectedStock ? selectedStock.id : null),
        stock_sku: primary?.stock_sku || (selectedStock ? selectedStock.sku : null),
        stock_name: primary?.stock_name || (selectedStock ? selectedStock.name : null),
        list_price: listPrice, discount_percent: discountPct, unit_cost: unitCost,
        karton_total: kartonTotalToSave,
        items,
        shipping_lines: shippingLines.map((s) => ({
          source: s.source,
          from_location: s.from_location,
          to_location: s.to_location,
          vehicle_name: s.vehicle_name,
          distance_km: s.distance_km,
          return_trip: !!s.return_trip,
          total_cost: s.total_cost,
          note: s.note || "",
        })),
        shipping_total: shippingTotal,
        shipping_per_unit: shippingPerUnit,
        comparisons: comparisons.map((c) => {
          const kSale = c.total_cost * (1 + marginPctNum / 100);
          const sTotal = kSale + (c.shipping_total || 0);
          return {
            key: c.key, supplier: c.supplier, supplier_valid_from: c.supplier_valid_from,
            dims: c.dims, quantity: c.quantity, flute: c.flute, quality: c.quality,
            unit: c.unit, total_m2: c.total_m2,
            total_cost: kSale, shipping_total: c.shipping_total || 0,
            shipping_lines_count: c.shipping_lines_count || 0,
            grand_total: sTotal,
          };
        }),
        grand_total: grandTotalToSave,
        mold_cost: Number(extras.mold_cost) || 0,
        cliche_cost: Number(extras.cliche_cost) || 0,
        extra_shipping_cost: Number(extras.extra_shipping_cost) || 0,
        pallet_cost_per_unit: Number(extras.pallet_cost_per_unit) || 0,
        pallet_deposit: !!extras.pallet_deposit,
        vat_rate: Number(terms.vat_rate) || 0,
        payment_term_days: Number(terms.payment_term_days) || 0,
        down_payment_pct: Number(terms.down_payment_pct) || 0,
        advance_payment_pct: Number(terms.advance_payment_pct) || 0,
      });
      const c = contacts.find((x) => x.id === selectedContactId);
      toast.success(`Teklif kaydedildi: ${c?.name || "cari"}${useCart ? ` (${cart.length} kalem)` : ""}`);
      if (useCart) clearCart();
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSavingQuote(false);
    }
  };

  const copySummary = async () => {
    if (!calc) return;
    const isPlaka = boxType === "plaka";
    const isHalfLid = boxType === "half_lid";
    const isFullLid = boxType === "full_lid";
    const header = isPlaka
      ? `Düz Plaka · ${fmtNum(calc.sheetWidth, 2)}×${fmtNum(calc.sheetLength, 2)} mm × ${quantity} adet`
      : isHalfLid
      ? `Yarım Kapak · ${L || 0}×${W || 0}×${H || 0} mm × ${quantity} adet`
      : isFullLid
      ? `Tam Kapak · ${L || 0}×${W || 0}×${H || 0} mm × ${quantity} adet`
      : `A Box · ${L || 0}×${W || 0}×${H || 0} mm × ${quantity} adet`;
    const txt =
      `${header}\n` +
      `Levha Eni:  ${fmtNum(calc.sheetWidth, 2)} mm\n` +
      `Levha Boyu: ${fmtNum(calc.sheetLength, 2)} mm\n` +
      `Adet m²:    ${fmtNum(calc.sheetM2, 6)} m²\n` +
      `Toplam m²:  ${fmtNum(totalM2, 4)} m²`;
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="pricing-page">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calculator size={20} />
            <h1 className="font-display text-3xl font-bold">Fiyat Hesapla</h1>
          </div>
          <p className="text-sm text-neutral-500">Koli türüne göre m² hesaplayıcı — A Box ile başlıyoruz.</p>
        </div>
        <button
          onClick={() => setManagerOpen(true)}
          data-testid="open-price-lists"
          hidden={isPricingOnly}
          className={`border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white flex items-center gap-1.5 ${isPricingOnly ? "hidden" : ""}`}
        >
          <Settings2 size={13} /> Fiyat Listelerini Yönet
        </button>
      </header>

      {/* Kutu tipi seçimi */}
      <div className="brutal-card p-4">
        <div className="label-caps mb-2">Kutu Tipi</div>
        <div className="flex flex-wrap gap-2">
          {BOX_TYPES.map((t) => (
            <button
              key={t.key}
              data-testid={`box-type-${t.key}`}
              onClick={() => {
                if (!t.enabled) return;
                if (t.key !== boxType) {
                  // Kutu tipi değiştirilince ilgisiz ölçü alanlarını temizle
                  if (t.key === "plaka") { setL(""); setW(""); setH(""); }
                  else { setPlakaW(""); setPlakaL(""); }
                }
                setBoxType(t.key);
              }}
              disabled={!t.enabled}
              className={`px-4 py-2 border-2 text-sm transition-colors ${
                boxType === t.key
                  ? "border-black bg-black text-white"
                  : t.enabled
                  ? "border-black/30 hover:border-black"
                  : "border-black/10 text-neutral-400 line-through cursor-not-allowed"
              }`}
            >
              {t.label}
              {!t.enabled && <span className="ml-2 text-[10px] uppercase tracking-widest">Yakında</span>}
            </button>
          ))}
        </div>
      </div>

      {/* KDV & Ödeme Koşulları — teklif metnine yansır */}
      <div className="brutal-card p-4" data-testid="quote-terms-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="label-caps">KDV & Ödeme Koşulları</div>
          <div className="text-[11px] text-neutral-500">— PDF'te ödeme paneline yazılır.</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="KDV Oranı (%)">
            <input type="number" step="0.1" min="0" max="100" className="input font-mono-num"
                   data-testid="term-vat"
                   value={terms.vat_rate}
                   onChange={(e) => setTerms({ ...terms, vat_rate: e.target.value })}
                   placeholder="20" />
          </F>
          <F label="Vade (gün)">
            <input type="number" step="1" min="0" className="input font-mono-num"
                   data-testid="term-days"
                   value={terms.payment_term_days}
                   onChange={(e) => setTerms({ ...terms, payment_term_days: e.target.value })}
                   placeholder="0 = peşin" />
          </F>
          <F label="Avans (%)">
            <input type="number" step="0.1" min="0" max="100" className="input font-mono-num"
                   data-testid="term-advance"
                   value={terms.advance_payment_pct}
                   onChange={(e) => setTerms({ ...terms, advance_payment_pct: e.target.value })}
                   placeholder="Sipariş anında" />
          </F>
          <F label="Peşinat (%)">
            <input type="number" step="0.1" min="0" max="100" className="input font-mono-num"
                   data-testid="term-down"
                   value={terms.down_payment_pct}
                   onChange={(e) => setTerms({ ...terms, down_payment_pct: e.target.value })}
                   placeholder="Teslim öncesi" />
          </F>
        </div>
      </div>

      {/* Ekstra Bedeller — teklife eklenir + PDF'te ayrı tablo */}
      <div className="brutal-card p-4" data-testid="quote-extras-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="label-caps">Ekstra Bedeller (Teklif Metnine Eklenecek)</div>
          <div className="text-[11px] text-neutral-500">— Sadece dolu olanlar PDF'e yazılır.</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="Kalıp Bedeli (TL)">
            <input type="number" step="0.01" min="0" className="input font-mono-num"
                   data-testid="extra-mold"
                   value={extras.mold_cost}
                   onChange={(e) => setExtras({ ...extras, mold_cost: e.target.value })}
                   placeholder="0,00" />
          </F>
          <F label="Klişe Bedeli (TL)">
            <input type="number" step="0.01" min="0" className="input font-mono-num"
                   data-testid="extra-cliche"
                   value={extras.cliche_cost}
                   onChange={(e) => setExtras({ ...extras, cliche_cost: e.target.value })}
                   placeholder="0,00" />
          </F>
          <F label="Nakliye Bedeli (TL)">
            <input type="number" step="0.01" min="0" className="input font-mono-num"
                   data-testid="extra-shipping"
                   value={extras.extra_shipping_cost}
                   onChange={(e) => setExtras({ ...extras, extra_shipping_cost: e.target.value })}
                   placeholder="0,00" />
          </F>
          <F label="Palet Bedeli (TL)">
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" min="0" className="input font-mono-num"
                     data-testid="extra-pallet"
                     value={extras.pallet_cost_per_unit}
                     onChange={(e) => setExtras({ ...extras, pallet_cost_per_unit: e.target.value })}
                     placeholder="0,00" />
              <label className="text-[10px] uppercase tracking-widest inline-flex items-center gap-1 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={extras.pallet_deposit}
                       data-testid="extra-pallet-deposit"
                       onChange={(e) => setExtras({ ...extras, pallet_deposit: e.target.checked })}
                       className="w-3 h-3 accent-black" />
                Depozitolu
              </label>
            </div>
          </F>
        </div>
      </div>

      {/* Cari eşleştirme + Teklife Kaydet — sadece admin görür */}
      {!isPricingOnly && (
      <div className="brutal-card p-4" data-testid="quote-bind-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <div className="label-caps mb-1.5">Cari (opsiyonel)</div>
            <select
              data-testid="quote-contact-select"
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              className="input bg-white"
            >
              <option value="">— Cari seç (teklife kaydetmek için) —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="text-[11px] text-neutral-500 mt-1">
              Bir cari seçip mevcut hesaplamayı (karton + nakliye + karşılaştırma) "teklif" olarak kaydedebilirsiniz.
            </div>
          </div>

          {/* Stoktan Seç */}
          <div className="min-w-[220px]">
            <div className="label-caps mb-1.5">Stok (opsiyonel)</div>
            {selectedStock ? (
              <div className="flex items-center gap-2 border-2 border-black bg-white px-3 py-2 h-[38px]" data-testid="selected-stock-chip">
                <Boxes size={14} />
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-mono-num font-semibold truncate">{selectedStock.sku}</div>
                  <div className="text-[10px] text-neutral-500 truncate">{selectedStock.name}</div>
                </div>
                <button
                  onClick={clearStock}
                  data-testid="clear-stock-btn"
                  className="p-0.5 hover:bg-rose-50 text-rose-600"
                  title="Stok bağını kaldır"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setStockPickerOpen(true)}
                data-testid="pick-stock-btn"
                disabled={stocks.length === 0}
                className="w-full border-2 border-black bg-white text-black hover:bg-black hover:text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed h-[38px]"
                title={stocks.length === 0 ? "Önce Stok modülünden ürün ekleyin" : "Stoktan seç"}
              >
                <Boxes size={13} /> Stoktan Seç ({stocks.length})
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => addCurrentToCart()}
              disabled={!calc || !selectedItem}
              data-testid="add-to-cart-btn"
              title="Bu ürünü teklif sepetine ekle"
              className="border-2 border-black bg-white text-black hover:bg-black hover:text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40 h-[38px]"
            >
              <Plus size={13} /> Sepete Ekle
            </button>
            <button
              onClick={saveQuote}
              disabled={!selectedContactId || savingQuote || (cart.length === 0 && (!calc || !selectedItem))}
              data-testid="save-quote-btn"
              className="brutal-btn bg-black text-white px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40"
            >
              <Save size={13} /> {savingQuote ? "Kaydediliyor…" : `Teklife Kaydet${cart.length > 0 ? ` (${cart.length} kalem)` : ""}`}
            </button>
          </div>
        </div>

        {/* Teklif Sepeti (çok kalemli teklif) */}
        {cart.length > 0 && (
          <div className="mt-4 border-2 border-black/20 bg-neutral-50" data-testid="quote-cart-panel">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 bg-white">
              <div className="label-caps flex items-center gap-1.5">
                <ShoppingCart size={12} /> Teklif Sepeti · {cart.length} ürün
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs">
                  Toplam:{" "}
                  <span className="font-mono-num font-semibold text-emerald-700">
                    {fmtNum(cartKartonTotal, 2)} ₺
                  </span>
                </div>
                <button
                  onClick={() => { if (window.confirm("Sepet boşaltılsın mı?")) clearCart(); }}
                  data-testid="clear-cart-btn"
                  className="text-[10px] uppercase tracking-widest px-2 py-1 border border-rose-600 text-rose-600 hover:bg-rose-50"
                >
                  Sepeti Boşalt
                </button>
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-neutral-100 border-b border-black/10">
                <tr>
                  <th className="text-left px-2 py-1.5 font-normal text-neutral-500">#</th>
                  <th className="text-left px-2 py-1.5 font-normal text-neutral-500">Ürün / Başlık</th>
                  <th className="text-left px-2 py-1.5 font-normal text-neutral-500">Ölçü</th>
                  <th className="text-right px-2 py-1.5 font-normal text-neutral-500">Adet</th>
                  <th className="text-right px-2 py-1.5 font-normal text-neutral-500">Karton Toplam</th>
                  <th className="w-20 px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((it, i) => (
                  <tr key={it._uid} className="border-b border-black/5 align-top" data-testid={`cart-row-${i}`}>
                    <td className="px-2 py-1.5 font-mono-num">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <div>
                        <span className="font-semibold">{it.flute} · {it.quality}</span>
                        {it.box_type === "plaka" ? <span className="text-neutral-500"> · Düz Plaka</span> : <span className="text-neutral-500"> · A Box</span>}
                        {it.stock_sku && <span className="text-neutral-500 ml-1">· {it.stock_sku}</span>}
                      </div>
                      <input
                        type="text"
                        placeholder="Başlık / açıklama (opsiyonel)…"
                        data-testid={`cart-title-${i}`}
                        value={it.title || ""}
                        onChange={(e) => updateCartItem(it._uid, { title: e.target.value })}
                        className="mt-1 w-full border border-black/10 bg-white px-1.5 py-0.5 text-[11px] outline-none focus:border-black"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono-num text-neutral-600">
                      {it.box_type === "plaka"
                        ? `${it.sheet_width_mm}×${it.sheet_length_mm} mm`
                        : `${it.length_mm}×${it.width_mm}×${it.height_mm} mm`}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num">{Number(it.quantity).toLocaleString("tr-TR")}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num font-semibold">
                      {fmtNum(it.karton_total, 2)} ₺
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() => editCartItem(it)}
                          data-testid={`cart-edit-${i}`}
                          className="text-blue-700 hover:bg-blue-50 p-1"
                          title="Düzenlemek için hesabına yükle"
                        >
                          <Settings2 size={12} />
                        </button>
                        <button
                          onClick={() => removeFromCart(it._uid)}
                          data-testid={`cart-remove-${i}`}
                          className="text-rose-600 hover:bg-rose-50 p-1"
                          title="Sepetten çıkar"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {(boxType === "abox" || boxType === "plaka" || boxType === "half_lid" || boxType === "full_lid") && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Girdi paneli */}
          <div className="md:col-span-2 brutal-card p-5 space-y-4" data-testid="abox-inputs">
            <div>
              <div className="label-caps mb-1">Ölçüler (mm)</div>
              <p className="text-[11px] text-neutral-500 leading-relaxed mb-3">
                {boxType === "plaka"
                  ? <>Levha <b>Eni</b> ve <b>Boyu</b>'nu doğrudan girin — kesime hazır düz plaka için m² hesaplanır.</>
                  : <><b>L</b> uzunluk, <b>W</b> en, <b>H</b> yükseklik</>}
              </p>
            </div>

            {boxType === "plaka" ? (
              <>
                <F label="Levha Eni (mm)">
                  <input
                    data-testid="plaka-width"
                    type="number" step="0.1" min="0"
                    value={plakaW} onChange={(e) => setPlakaW(e.target.value)}
                    className="input font-mono-num" placeholder="örn. 1330"
                  />
                </F>
                <F label="Levha Boyu (mm)">
                  <input
                    data-testid="plaka-length"
                    type="number" step="0.1" min="0"
                    value={plakaL} onChange={(e) => setPlakaL(e.target.value)}
                    className="input font-mono-num" placeholder="örn. 456"
                  />
                </F>
              </>
            ) : (
              <>
                <F label="Uzunluk · L (mm)">
                  <input
                    data-testid="abox-length"
                    type="number" step="0.1" min="0"
                    value={L} onChange={(e) => setL(e.target.value)}
                    className="input font-mono-num" placeholder="örn. 300"
                  />
                </F>
                <F label="En · W (mm)">
                  <input
                    data-testid="abox-width"
                    type="number" step="0.1" min="0"
                    value={W} onChange={(e) => setW(e.target.value)}
                    className="input font-mono-num" placeholder="örn. 200"
                  />
                </F>
                <F label="Yükseklik · H (mm)">
                  <input
                    data-testid="abox-height"
                    type="number" step="0.1" min="0"
                    value={H} onChange={(e) => setH(e.target.value)}
                    className="input font-mono-num" placeholder="örn. 150"
                  />
                </F>
              </>
            )}
            <F label="Adet">
              <input
                data-testid="abox-qty"
                type="number" step="1" min="1"
                value={qty} onChange={(e) => setQty(e.target.value)}
                className="input font-mono-num"
              />
            </F>

            {(boxType === "abox" || boxType === "half_lid" || boxType === "full_lid") && (
              <label
                className="flex items-center gap-2.5 cursor-pointer select-none border-2 border-black/15 hover:border-black/40 p-2.5 transition-colors"
                data-testid="double-piece-toggle"
              >
                <input
                  type="checkbox"
                  checked={doublePiece}
                  onChange={(e) => setDoublePiece(e.target.checked)}
                  className="w-4 h-4 accent-black"
                  data-testid="double-piece-checkbox"
                />
                <div className="flex-1">
                  <div className="text-xs font-semibold">Koli çift parça mı?</div>
                  <div className="text-[10px] text-neutral-500">İşaretlendiğinde Levha Boyu'na +30 mm eklenir.</div>
                </div>
                {doublePiece && (
                  <span className="text-[10px] font-mono-num font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5">
                    +30 mm
                  </span>
                )}
              </label>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={reset}
                data-testid="abox-reset"
                className="flex-1 border border-black/30 py-2 text-xs uppercase tracking-widest hover:bg-neutral-50"
              >Temizle</button>
              <button
                onClick={copySummary}
                data-testid="abox-copy"
                disabled={!calc}
                className="flex-1 border border-black bg-black text-white py-2 text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-1.5"
              >
                <Copy size={13} /> Sonucu Kopyala
              </button>
            </div>
          </div>

          {/* Sonuç paneli */}
          <div className="md:col-span-3 space-y-4">
            {/* Formül kartı */}
            <div className="brutal-card p-5">
              <div className="label-caps mb-3">Kullanılan Formül</div>
              <div className="space-y-1.5 text-xs font-mono-num text-neutral-700">
                {boxType === "plaka" ? (
                  <>
                    <div>Levha Eni  = kullanıcı girdisi</div>
                    <div>Levha Boyu = kullanıcı girdisi</div>
                    <div>Levha m²   = Eni × Boyu / 1.000.000</div>
                  </>
                ) : boxType === "half_lid" ? (
                  <>
                    <div data-testid="formula-sheet-width">Levha Eni  = (W / 2) + H + 3</div>
                    <div>Levha Boyu = ((L + W) × 2) + 30{doublePiece ? " + 30" : ""}</div>
                    <div>Levha m²   = Eni × Boyu / 1.000.000</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-[10px] uppercase tracking-widest bg-sky-100 border border-sky-400 text-sky-800 px-1.5 py-0.5">Yarım Kapak</span>
                      {doublePiece && (
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-100 border border-emerald-400 text-emerald-800 px-1.5 py-0.5">Çift Parça · +30</span>
                      )}
                    </div>
                  </>
                ) : boxType === "full_lid" ? (
                  <>
                    <div data-testid="formula-sheet-width">Levha Eni  = W + H</div>
                    <div>Levha Boyu = ((L + W) × 2) + 30{doublePiece ? " + 30" : ""}</div>
                    <div>Levha m²   = Eni × Boyu / 1.000.000</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-[10px] uppercase tracking-widest bg-violet-100 border border-violet-400 text-violet-800 px-1.5 py-0.5">Tam Kapak</span>
                      {doublePiece && (
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-100 border border-emerald-400 text-emerald-800 px-1.5 py-0.5">Çift Parça · +30</span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div data-testid="formula-sheet-width">
                      Levha Eni  = W + H + {calc?.eniPad ?? 2}
                      {calc?.flute === "BC" && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest bg-amber-100 border border-amber-400 text-amber-800 px-1.5 py-0.5">BC dalga: +4</span>
                      )}
                    </div>
                    <div>Levha Boyu = ((L + W) × 2) + 30{doublePiece ? " + 30" : ""}</div>
                    <div>Levha m²   = Eni × Boyu / 1.000.000</div>
                    {doublePiece && (
                      <div className="mt-1">
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-100 border border-emerald-400 text-emerald-800 px-1.5 py-0.5">Çift Parça · +30</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Ara sonuçlar */}
            <div className="grid grid-cols-2 gap-3">
              <ResultCard
                testid="result-sheet-width"
                label="Levha Eni"
                value={calc ? fmtNum(calc.sheetWidth, 2) : "—"}
                unit="mm"
                formula={
                  boxType === "plaka" ? "kullanıcı girdisi"
                  : boxType === "half_lid" ? `(${W || 0} / 2) + ${H || 0} + 3`
                  : boxType === "full_lid" ? `${W || 0} + ${H || 0}`
                  : `${W || 0} + ${H || 0} + ${calc?.eniPad ?? 2}`
                }
              />
              <ResultCard
                testid="result-sheet-length"
                label="Levha Boyu"
                value={calc ? fmtNum(calc.sheetLength, 2) : "—"}
                unit="mm"
                formula={
                  boxType === "plaka" ? "kullanıcı girdisi"
                  : boxType === "half_lid" ? `((${L || 0} + ${W || 0}) × 2) + 30${doublePiece ? " + 30" : ""}`
                  : `((${L || 0} + ${W || 0}) × 2) + 30${doublePiece ? " + 30" : ""}`
                }
              />
            </div>

            {/* Ana sonuç: m² */}
            <div className="brutal-card p-5 bg-black text-white" data-testid="result-summary">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
                    Adet Başına m²
                  </div>
                  <div className="font-mono-num text-3xl font-bold" data-testid="result-sheet-m2">
                    {calc ? fmtNum(calc.sheetM2, 6) : "—"}
                    <span className="text-base font-normal ml-1">m²</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
                    Toplam m² ({quantity.toLocaleString("tr-TR")} adet)
                  </div>
                  <div className="font-mono-num text-3xl font-bold text-emerald-400" data-testid="result-total-m2">
                    {calc ? fmtNum(totalM2, 4) : "—"}
                    <span className="text-base font-normal ml-1">m²</span>
                  </div>
                </div>
              </div>

              {/* Ağırlık — kalite seçildiyse ve gramaj biliniyorsa göster */}
              {(() => {
                const gsm = Number(selectedItem?.gsm) || 0;
                if (!calc || !gsm) return null;
                const unitGrams = calc.sheetM2 * gsm;
                const totalGrams = totalM2 * gsm;
                const fmtWeight = (g) => g >= 1000
                  ? `${fmtNum(g / 1000, 3)} kg`
                  : `${fmtNum(g, 1)} g`;
                return (
                  <div className="grid grid-cols-2 gap-6 mt-4 pt-4 border-t border-white/15" data-testid="result-weight">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
                        Adet Başına Ağırlık
                      </div>
                      <div className="font-mono-num text-2xl font-bold" data-testid="result-unit-weight">
                        {fmtWeight(unitGrams)}
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5 font-mono-num">
                        {fmtNum(calc.sheetM2, 4)} m² × {gsm} g/m²
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">
                        Toplam Ağırlık
                      </div>
                      <div className="font-mono-num text-2xl font-bold text-amber-300" data-testid="result-total-weight">
                        {fmtWeight(totalGrams)}
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5 font-mono-num">
                        {fmtNum(totalM2, 4)} m² × {gsm} g/m²
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <p className="text-[11px] text-amber-800 leading-relaxed px-3 py-2 bg-amber-50 border-l-4 border-amber-400">
              <b>Not:</b> Bu gramaj kâğıt metriklerinin verdiği sonuçtur. Koli gramajı tutkal vb.
              kimyasallar nedeniyle <b>%5-10 arasında</b> fazla gelebilir.
            </p>

            {/* Karton fiyat listesi entegrasyonu */}
            <div className="brutal-card p-5 space-y-3" data-testid="karton-cost-card">
              <div className="flex items-center justify-between">
                <div className="label-caps">Karton Maliyeti (Tedarikçi Fiyat Listesinden)</div>
                {(() => {
                  const visibleLists = priceLists.filter((l) => l.is_active !== false);
                  return visibleLists.length > 1 && (
                    <select
                      data-testid="active-list-select"
                      value={activeListId || ""}
                      onChange={(e) => setActiveListId(e.target.value)}
                      className="text-xs border border-black/20 px-2 py-1 bg-white"
                    >
                      {visibleLists.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.supplier_name}{l.is_default ? " (varsayılan)" : ""} · {l.valid_from}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>

              {activeListItems.length === 0 ? (
                <div className="text-xs text-neutral-500 border border-dashed border-black/20 p-4 text-center">
                  Henüz fiyat listesi yüklenmemiş. Üstteki <b>"Fiyat Listelerini Yönet"</b> butonundan Excel şablonuyla bir liste ekleyin.
                </div>
              ) : (
                <>
                  <select
                    data-testid="karton-select"
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    className="input bg-white"
                  >
                    <option value="">— Karton türü seçin —</option>
                    {activeListItems.map((it) => (
                      <option key={it.id} value={it.id}>
                        {isPricingOnly
                          ? `${it.flute} · ${it.quality}`
                          : `${it.flute} · ${it.quality} · ${Number(it.price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL/${it.unit}`}
                      </option>
                    ))}
                  </select>

                  {selectedItem && calc && (
                    <div className="space-y-3 pt-2">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="border border-black/10 p-3">
                          <div className="label-caps text-[10px]">Liste Fiyatı</div>
                          <div className="font-mono-num text-lg font-semibold mt-0.5" data-testid="karton-list-price">
                            {fmtNum(listPrice, 2)} <span className="text-xs font-normal text-neutral-500">TL/{selectedItem.unit}</span>
                          </div>
                          {discountPct > 0 && (
                            <div className="text-[10px] text-emerald-700 mt-0.5">İskonto sonrası: {fmtNum(unitCost, 2)}</div>
                          )}
                        </div>
                        <div className="border border-black/10 p-3">
                          <div className="label-caps text-[10px]">İskonto</div>
                          <div className={`font-mono-num text-lg font-semibold mt-0.5 ${discountPct > 0 ? "text-emerald-700" : "text-neutral-400"}`} data-testid="karton-discount">
                            %{fmtNum(discountPct, 2)}
                          </div>
                          {discountPct > 0 && (
                            <div className="text-[10px] text-emerald-700 mt-0.5">Kazanç: {fmtNum(discountSavings, 2)} TL</div>
                          )}
                        </div>
                        <div className="border border-black/10 p-3">
                          <div className="label-caps text-[10px]">Adet Başına</div>
                          <div className="font-mono-num text-lg font-semibold mt-0.5" data-testid="karton-per-unit">
                            {fmtNum(calc.sheetM2 * unitCost, 2)} <span className="text-xs font-normal text-neutral-500">TL</span>
                          </div>
                        </div>
                      </div>
                      <div className="border-2 border-black p-4 bg-emerald-50" data-testid="karton-total-card">
                        <div className="flex items-center justify-between">
                          <div className="label-caps">Toplam Karton Maliyeti</div>
                          <div className="text-right">
                            <div className="font-mono-num text-2xl font-bold text-emerald-700" data-testid="karton-total-cost">
                              {fmtNum(totalCost, 2)} <span className="text-sm font-normal">TL</span>
                            </div>
                            {discountPct > 0 && (
                              <div className="text-[11px] text-neutral-600 mt-0.5">
                                Liste: {fmtNum(totalM2 * listPrice, 2)} TL · İskonto ({fmtNum(discountPct, 2)}%): −{fmtNum(discountSavings, 2)} TL
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-black/10 flex items-center justify-between gap-2">
                          <div className="text-[11px] text-neutral-600">
                            Farklı tedarikçileri karşılaştırmak için bu sonucu sabitleyin, sonra tedarikçiyi değiştirip tekrar ekleyin.
                          </div>
                          <button
                            onClick={pinComparison}
                            data-testid="pin-comparison-btn"
                            className="border-2 border-black bg-white text-black hover:bg-black hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 whitespace-nowrap"
                          >
                            <PlusCircle size={13} /> Karşılaştırmaya Ekle
                          </button>
                        </div>
                      </div>

                      {/* Nakliye bölümü */}
                      <div className="brutal-card p-4 space-y-3" data-testid="shipping-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 label-caps">
                            <Truck size={13} /> Nakliye Satırları
                            {shippingLines.length > 0 && (
                              <span className="text-[10px] normal-case tracking-normal text-neutral-500">
                                ({shippingLines.length} satır)
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => setShipPickerOpen(true)}
                            data-testid="add-shipping-btn"
                            className="border-2 border-black bg-white text-black hover:bg-black hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
                          >
                            <PlusCircle size={13} /> Nakliye Ekle
                          </button>
                        </div>

                        {shippingLines.length === 0 ? (
                          <div className="text-[11px] text-neutral-500 border border-dashed border-black/20 p-3 text-center">
                            Nakliye eklenmedi. Karton maliyetine kutu başı nakliye eklemek için "Nakliye Ekle" butonuna basın.
                          </div>
                        ) : (
                          <div className="divide-y divide-black/5 border border-black/10">
                            {shippingLines.map((s, idx) => (
                              <div key={s._uid} data-testid={`shipping-row-${idx}`}
                                   className="flex items-center justify-between gap-2 px-3 py-2">
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-semibold truncate">
                                    {s.from_location} → {s.to_location}
                                  </div>
                                  <div className="text-[10px] text-neutral-500 mt-0.5">
                                    {s.vehicle_name} · {s.distance_km} km{s.return_trip ? " ×2" : ""}
                                    {s.source === "history" && <span className="ml-1.5 uppercase tracking-widest text-[9px] bg-neutral-100 border border-black/10 px-1 py-0.5">Geçmiş</span>}
                                    {s.source === "quick+saved" && <span className="ml-1.5 uppercase tracking-widest text-[9px] bg-emerald-50 border border-emerald-500 text-emerald-700 px-1 py-0.5">Kaydedildi</span>}
                                  </div>
                                </div>
                                <div className="font-mono-num text-sm font-semibold whitespace-nowrap">
                                  {fmtNum(s.total_cost, 2)} <span className="text-xs font-normal text-neutral-500">TL</span>
                                </div>
                                <button
                                  onClick={() => removeShippingLine(idx)}
                                  data-testid={`shipping-remove-${idx}`}
                                  className="p-1 text-rose-600 hover:bg-rose-50"
                                  title="Kaldır"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {shippingLines.length > 0 && (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="border border-black/10 p-3">
                              <div className="label-caps text-[10px]">Nakliye Toplamı</div>
                              <div className="font-mono-num text-lg font-semibold mt-0.5" data-testid="shipping-total">
                                {fmtNum(shippingTotal, 2)} <span className="text-xs font-normal text-neutral-500">TL</span>
                              </div>
                            </div>
                            <div className="border border-black/10 p-3">
                              <div className="label-caps text-[10px]">Kutu Başı Nakliye</div>
                              <div className="font-mono-num text-lg font-semibold mt-0.5" data-testid="shipping-per-unit">
                                {fmtNum(shippingPerUnit, 4)} <span className="text-xs font-normal text-neutral-500">TL/adet</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Genel Toplam (Karton + Nakliye) */}
                      {/* Kâr Marjı Matrisi */}
                      <div className="brutal-card p-4 space-y-3" data-testid="margin-card">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 label-caps">
                            <TrendingUp size={13} /> Kâr Marjı
                            <span className="text-[10px] normal-case tracking-normal text-neutral-500">(yalnız kartona; nakliye sabit)</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Özel:</label>
                            <input
                              type="number" step="0.5" min="0"
                              value={marginPct}
                              onChange={(e) => setMarginPct(e.target.value)}
                              data-testid="margin-custom-input"
                              className="border border-black/20 px-2 py-1 w-20 text-xs font-mono-num text-right"
                            />
                            <span className="text-xs text-neutral-500">%</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {MARGIN_STEPS.map((m) => {
                            const active = Number(marginPct) === m;
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => setMarginPct(m)}
                                data-testid={`margin-step-${m}`}
                                className={`px-3 py-1.5 text-xs font-mono-num border-2 transition-colors ${
                                  active ? "border-black bg-black text-white" : "border-black/20 hover:border-black"
                                }`}
                              >
                                %{fmtNum(m, m % 1 === 0 ? 0 : 1)}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Maliyet & Satış Kartı (marj sonrası) */}
                      <div className="border-2 border-black p-4 bg-black text-white" data-testid="grand-total-card">
                        <div className="grid grid-cols-2 gap-4 divide-x divide-white/20">
                          <div className="pr-4">
                            <div className="label-caps text-white/60 mb-1.5">Maliyet</div>
                            <div className="font-mono-num text-xl font-bold text-white" data-testid="grand-total-cost">
                              {fmtNum(grandTotal, 2)} <span className="text-xs font-normal text-white/70">TL</span>
                            </div>
                            <div className="text-[10px] text-white/50 mt-1 space-y-0.5">
                              <div>Karton: {fmtNum(totalCost, 2)}</div>
                              {shippingTotal > 0 && <div>Nakliye: {fmtNum(shippingTotal, 2)}</div>}
                              <div>Kutu başı: {fmtNum(quantity > 0 ? grandTotal / quantity : 0, 4)}</div>
                              {totalM2 > 0 && (
                                <div data-testid="cost-per-m2">
                                  Birim maliyet: <b className="font-mono-num">{fmtNum(totalCost / totalM2, 3)}</b> <span className="text-white/40">₺/m²</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="pl-4">
                            <div className="label-caps text-emerald-300/80 mb-1.5">
                              Satış (Kâr %{fmtNum(marginPctNum, marginPctNum % 1 === 0 ? 0 : 1)})
                            </div>
                            <div className="font-mono-num text-2xl font-bold text-emerald-400" data-testid="sale-grand-total">
                              {fmtNum(saleGrandTotal, 2)} <span className="text-sm font-normal">TL</span>
                            </div>
                            <div className="text-[10px] text-white/70 mt-1 space-y-0.5">
                              <div>Karton (Satış): {fmtNum(kartonSalePrice, 2)}</div>
                              {shippingTotal > 0 && (
                                <div>Nakliye: {fmtNum(shippingTotal, 2)} <span className="text-white/40">(sabit)</span></div>
                              )}
                              <div className="text-emerald-300" data-testid="profit-tl">Kâr: {fmtNum(profitTL, 2)} TL</div>
                              <div>Kutu başı satış: <b className="font-mono-num" data-testid="sale-per-unit">{fmtNum(salePerUnit, 4)}</b></div>
                              {totalM2 > 0 && (
                                <div data-testid="sale-per-m2">
                                  Birim satış: <b className="font-mono-num text-emerald-300">{fmtNum(kartonSalePrice / totalM2, 3)}</b> <span className="text-white/40">₺/m²</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {managerOpen && !isPricingOnly && (
        <PriceListsManager
          onClose={() => setManagerOpen(false)}
          onChange={loadPriceData}
        />
      )}

      {shipPickerOpen && (
        <ShippingPickerModal
          onClose={() => setShipPickerOpen(false)}
          onAdd={addShippingLine}
        />
      )}

      {stockPickerOpen && (
        <StockPickerModal
          stocks={stocks}
          contacts={contacts}
          onClose={() => setStockPickerOpen(false)}
          onPick={(s) => { applyStock(s); setStockPickerOpen(false); }}
        />
      )}

      {comparisons.length > 0 && (
        <div className="brutal-card overflow-hidden" data-testid="comparison-panel">
          <div className="p-5 border-b border-black/10 flex items-center justify-between">
            <div>
              <div className="label-caps">Tedarikçi Karşılaştırma Tablosu</div>
              <div className="text-xs text-neutral-500 mt-1">
                {comparisons.length} sabitlenmiş sonuç
                {cheapestKey && <span className="ml-2 text-emerald-700 font-semibold">· En ucuz emerald ile işaretli</span>}
              </div>
            </div>
            <button
              onClick={clearAllPins}
              data-testid="clear-all-comparisons"
              className="border border-rose-400 text-rose-600 hover:bg-rose-600 hover:text-white px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5"
            >
              <RotateCcw size={12} /> Tümünü Temizle
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="comparison-table">
              <thead className="bg-neutral-50">
                <tr className="border-b-2 border-black/60">
                  <th className="text-left px-3 py-2 label-caps">Tedarikçi</th>
                  <th className="text-left px-3 py-2 label-caps">Ölçü × Adet</th>
                  <th className="text-left px-3 py-2 label-caps">Karton</th>
                  <th className="text-right px-3 py-2 label-caps">Liste Fiyatı</th>
                  <th className="text-right px-3 py-2 label-caps">İskonto</th>
                  <th className="text-right px-3 py-2 label-caps">Birim (net)</th>
                  <th className="text-right px-3 py-2 label-caps">Toplam m²</th>
                  <th className="text-right px-3 py-2 label-caps">Karton Maliyet</th>
                  <th className="text-right px-3 py-2 label-caps">Nakliye TL</th>
                  <th className="text-right px-3 py-2 label-caps">Maliyet</th>
                  <th className="text-right px-3 py-2 label-caps">Marj %</th>
                  <th className="text-right px-3 py-2 label-caps">Kâr TL</th>
                  <th className="text-right px-3 py-2 label-caps">Satış Toplam</th>
                  <th className="text-right px-3 py-2 label-caps">Fark</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((c) => {
                  const isCheapest = c.key === cheapestKey;
                  const cKartonSale = c.total_cost * (1 + marginPctNum / 100);
                  const cProfit = cKartonSale - c.total_cost;
                  const cSaleTotal = cKartonSale + (c.shipping_total || 0);
                  const cheapest = comparisons.find((x) => x.key === cheapestKey) || c;
                  const cheapestSale = cheapest.total_cost * (1 + marginPctNum / 100) + (cheapest.shipping_total || 0);
                  const diff = cSaleTotal - cheapestSale;
                  return (
                    <tr key={c.key} className={`border-b border-black/5 ${isCheapest ? "bg-emerald-50" : "hover:bg-neutral-50"}`} data-testid={`comparison-row-${c.key}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold flex items-center gap-1.5">
                          {isCheapest && <span className="text-[9px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5">En Ucuz</span>}
                          {c.supplier}
                        </div>
                        <div className="text-[10px] text-neutral-500">{c.supplier_valid_from}</div>
                      </td>
                      <td className="px-3 py-2 font-mono-num">
                        <div>{c.dims} mm</div>
                        <div className="text-[10px] text-neutral-500">× {Number(c.quantity).toLocaleString("tr-TR")} adet</div>
                      </td>
                      <td className="px-3 py-2">
                        <div><span className="font-semibold">{c.flute}</span> · {c.quality}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtNum(c.list_price, 2)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">
                        {c.discount_percent > 0
                          ? <span className="text-emerald-700 font-semibold">%{fmtNum(c.discount_percent, 2)}</span>
                          : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num font-semibold">{fmtNum(c.unit_cost, 2)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtNum(c.total_m2, 4)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmtNum(c.total_cost, 2)}</td>
                      <td className="px-3 py-2 text-right font-mono-num" data-testid={`comparison-shipping-${c.key}`}>
                        {c.shipping_total > 0 ? (
                          <div>
                            <div>{fmtNum(c.shipping_total, 2)}</div>
                            <div className="text-[9px] text-neutral-500">{c.shipping_lines_count} satır</div>
                          </div>
                        ) : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num" data-testid={`comparison-total-${c.key}`}>
                        {fmtNum(c.grand_total, 2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-neutral-500">
                        %{fmtNum(marginPctNum, marginPctNum % 1 === 0 ? 0 : 1)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-emerald-700" data-testid={`comparison-profit-${c.key}`}>
                        {fmtNum(cProfit, 2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-base font-bold text-emerald-700" data-testid={`comparison-sale-${c.key}`}>
                        {fmtNum(cSaleTotal, 2)}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono-num ${diff > 0 ? "text-rose-600" : "text-neutral-400"}`}>
                        {isCheapest || Math.abs(diff) < 0.005 ? "—" : `+${fmtNum(diff, 2)}`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => removePin(c.key)} data-testid={`remove-comparison-${c.key}`} className="text-rose-600 p-1"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border: 1px solid rgba(0,0,0,0.2);
          padding: 8px 10px;
          font-size: 14px;
          outline: none;
          background: white;
        }
        .input:focus { border-color: black; box-shadow: 0 0 0 2px black; }
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ResultCard({ testid, label, value, unit, formula }) {
  return (
    <div className="brutal-card p-4" data-testid={testid}>
      <div className="label-caps text-[10px]">{label}</div>
      <div className="font-mono-num text-2xl font-bold mt-1">
        {value}
        {value !== "—" && <span className="text-sm font-normal text-neutral-500 ml-1">{unit}</span>}
      </div>
      <div className="text-[10px] text-neutral-400 font-mono-num mt-1">{formula}</div>
    </div>
  );
}
